import { createSyncSignal, clearSyncData } from '@solidjs/sync';
import { warn, debug } from '../utils/console';
import { isRenderYieldActive } from '../utils/renderYield';
import { createStallBackoff, LIVE_POLL_BASE_MS, LIVE_POLL_MAX_MS } from '../utils/liveStallBackoff';
import { createEffect, onCleanup, createRoot, untrack, createSignal } from 'solid-js';
import { selectedEvents, selectedRange, setSelectedRange, isCut, cutEvents } from './selectionStore';
import { apiEndpoints } from '@config/env';
import { getData } from '../utils/global';
import { liveConfigStore } from './liveConfigStore';
import { liveSourcesStore } from './liveSourcesStore';
import { streamingStore } from './streamingStore';
import { liveColumnarStore } from './liveColumnarStore';
import { config } from '@config/env';

// Type definitions
export interface PlaybackState {
  showPlayback: boolean;
  selectedTime: Date;
  isPlaying: boolean;
  playbackSpeed: number;
  timeWindow: number; // Time window in minutes
  videoTime: Date | null;
  playbackInterval: number | null;
  isManualTimeChange: boolean; // Flag to track manual time changes (brush, track click)
  activeComponent: string | null; // Track which component is currently controlling selectedTime
}

export type SyncFunction = () => void;

/** Seconds to add to maneuver datetime to get video clip start. Negative = start before maneuver time. */
export const MANEUVER_VIDEO_START_OFFSET_SECONDS = -15;

// Helper function to safely call sync functions and handle BroadcastChannel errors
const safeSync = (syncFn: (() => void) | undefined, name: string = 'sync'): void => {
  if (typeof syncFn === "function") {
    try {
      syncFn();
    } catch (error: any) {
      // BroadcastChannel might be closed (e.g., during page unload or tab close)
      if (error?.message?.includes('BroadcastChannel') || error?.message?.includes('Channel is closed')) {
        debug(`🔄 ${name} skipped - BroadcastChannel is closed`);
      } else {
        warn(`Error in ${name}:`, error);
      }
    }
  }
};
// Safe accessor to avoid TS issues when upstream typing is too loose
const getActiveComponentName = (): string | null => {
  try {
    const val: any = activeComponent();
    return typeof val === 'string' ? val : null;
  } catch {
    return null;
  }
};

// Safe boolean getter for manual-change flag
const getIsManual = (): boolean => {
  try {
    const fn: any = isManualTimeChange as any;
    const val = typeof fn === 'function' ? fn() : fn;
    return !!val;
  } catch {
    return false;
  }
};

// Declare all signal variables first
let showPlaybackState: any;
let setShowPlaybackState: any;
let syncShowPlayback: any;
let selectedTimeState: any;
let setSelectedTimeState: any;
let syncSelectedTime: any;
let isPlayingState: any;
let setIsPlayingState: any;
let syncIsPlaying: any;
let playbackSpeedState: any;
let setPlaybackSpeedState: any;
let syncPlaybackSpeed: any;
let timeWindowState: any;
let setTimeWindowState: any;
let syncTimeWindow: any;
let videoTimeState: any;
let setVideoTimeState: any;
let syncVideoTime: any;
let playbackIntervalState: any;
let setPlaybackIntervalState: any;
let isManualTimeChangeState: any;
let setIsManualTimeChangeState: any;
let syncIsManualTimeChange: any;
let shouldRestartPlaybackState: any;
let setShouldRestartPlaybackState: any;
let activeComponentState: any;
let setActiveComponentState: any;
let syncActiveComponent: any;
let liveModeState: any;
let setLiveModeState: any;
let liveFollowState: any;
let setLiveFollowState: any;
/** Synced registry: instanceId -> last heartbeat ms (cross-window map presence). */
type MapInstanceRegistry = Record<string, number>;

const MAP_INSTANCE_STALE_MS = 15_000;
const MAP_INSTANCE_HEARTBEAT_MS = 5_000;

/** Drop entries whose last heartbeat is older than the stale threshold. */
function pruneMapInstances(registry: MapInstanceRegistry | null | undefined): MapInstanceRegistry {
  const now = Date.now();
  const out: MapInstanceRegistry = {};
  if (!registry || typeof registry !== "object") return out;
  for (const [id, ts] of Object.entries(registry)) {
    const heartbeat = Number(ts);
    if (id && Number.isFinite(heartbeat) && now - heartbeat < MAP_INSTANCE_STALE_MS) {
      out[id] = heartbeat;
    }
  }
  return out;
}

function createMapInstanceId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface MapInstanceController {
  /** Live (non-stale) instance count across windows. */
  count: () => number;
  /** Register a mount; returns cleanup to call on unmount. Heartbeats keep presence fresh. */
  register: () => (() => void);
  /** @deprecated paired increment for callers not yet migrated to register(). */
  increment: () => void;
  /** @deprecated paired decrement for callers not yet migrated to register(). */
  decrement: () => void;
  /** Remove every locally-owned instance (used on store disposal). */
  disposeLocal: () => void;
}

/**
 * Builds a cross-window map-presence controller backed by a heartbeat registry.
 * Stale entries self-expire, so a missed unmount (hard reload / tab close, or a
 * cross-window sync clobbering a shared numeric counter) never leaves the map
 * counted as visible.
 */
function createMapInstanceController(
  getState: () => MapInstanceRegistry,
  setState: (updater: (prev: MapInstanceRegistry) => MapInstanceRegistry) => void,
  getSync: () => (() => void) | undefined,
  syncName: string,
  idPrefix: string
): MapInstanceController {
  const heartbeatTimers = new Map<string, ReturnType<typeof setInterval>>();
  const legacyStack: string[] = [];

  const touch = (id: string): void => {
    setState((prev) => ({ ...pruneMapInstances(prev), [id]: Date.now() }));
    safeSync(getSync(), syncName);
  };

  const remove = (id: string): void => {
    const timer = heartbeatTimers.get(id);
    if (timer) {
      clearInterval(timer);
      heartbeatTimers.delete(id);
    }
    setState((prev) => {
      const next = { ...(prev || {}) };
      delete next[id];
      return next;
    });
    safeSync(getSync(), syncName);
  };

  const startHeartbeat = (id: string): void => {
    touch(id);
    const timer = setInterval(() => touch(id), MAP_INSTANCE_HEARTBEAT_MS);
    heartbeatTimers.set(id, timer);
  };

  return {
    count: () => Object.keys(pruneMapInstances(getState())).length,
    register: () => {
      const id = createMapInstanceId(idPrefix);
      startHeartbeat(id);
      return () => remove(id);
    },
    increment: () => {
      const id = createMapInstanceId(idPrefix);
      legacyStack.push(id);
      startHeartbeat(id);
    },
    decrement: () => {
      const id = legacyStack.pop();
      if (!id) return;
      remove(id);
    },
    disposeLocal: () => {
      for (const id of Array.from(heartbeatTimers.keys())) {
        remove(id);
      }
    },
  };
}

let fleetMapInstancesState: any;
let setFleetMapInstancesState: any;
let syncFleetMapInstances: any;
let exploreMapInstancesState: any;
let setExploreMapInstancesState: any;
let syncExploreMapInstances: any;

const fleetMapInstanceController = createMapInstanceController(
  () => fleetMapInstancesState() as MapInstanceRegistry,
  (updater) => setFleetMapInstancesState(updater),
  () => syncFleetMapInstances,
  "syncFleetMapInstances",
  "fleet-map"
);

const exploreMapInstanceController = createMapInstanceController(
  () => exploreMapInstancesState() as MapInstanceRegistry,
  (updater) => setExploreMapInstancesState(updater),
  () => syncExploreMapInstances,
  "syncExploreMapInstances",
  "explore-map"
);
// Initialize all signals within a createRoot to avoid cleanup warnings
createRoot(() => {
  [showPlaybackState, setShowPlaybackState, syncShowPlayback] = createSyncSignal(true, {
    key: "showPlayback",
    autoSync: true
  });
  [selectedTimeState, setSelectedTimeState, syncSelectedTime] = createSyncSignal(new Date('1970-01-01T12:00:00Z'), {
    key: "selectedTime",
    autoSync: true
  });
  [isPlayingState, setIsPlayingState, syncIsPlaying] = createSyncSignal(false, {
    key: "isPlaying",
    autoSync: true
  });
  [playbackSpeedState, setPlaybackSpeedState, syncPlaybackSpeed] = createSyncSignal(1, {
    key: "playbackSpeed",
    autoSync: true
  });
  [timeWindowState, setTimeWindowState, syncTimeWindow] = createSyncSignal(0, {
    key: "timeWindow",
    autoSync: true
  });
  [videoTimeState, setVideoTimeState, syncVideoTime] = createSyncSignal(null, {
    key: "videoTime",
    autoSync: true
  });
  [playbackIntervalState, setPlaybackIntervalState] = createSyncSignal(null, {
    key: "playbackInterval",
    autoSync: false // No need to sync intervals across clients
  });
  [isManualTimeChangeState, setIsManualTimeChangeState, syncIsManualTimeChange] = createSyncSignal<boolean>(false, {
    key: "isManualTimeChange",
    autoSync: true
  });
  [shouldRestartPlaybackState, setShouldRestartPlaybackState] = createSyncSignal<boolean>(false, {
    key: "shouldRestartPlayback",
    autoSync: true
  });
  [activeComponentState, setActiveComponentState, syncActiveComponent] = createSyncSignal<string | null>(null, {
    key: "activeComponent",
    autoSync: true
  });
  [liveModeState, setLiveModeState] = createSyncSignal<boolean>(false, {
    key: "liveMode",
    autoSync: true
  });
  // "Follow the live edge / auto-select latest" master switch for live Performance & Maneuvers.
  // Deliberately distinct from isPlaying (which ManeuverPlayPause reuses for maneuver-clip playback).
  [liveFollowState, setLiveFollowState] = createSyncSignal<boolean>(true, {
    key: "liveFollow",
    autoSync: true
  });
  [fleetMapInstancesState, setFleetMapInstancesState, syncFleetMapInstances] = createSyncSignal<MapInstanceRegistry>({}, {
    key: "fleetMapInstances",
    autoSync: true
  });
  [exploreMapInstancesState, setExploreMapInstancesState, syncExploreMapInstances] = createSyncSignal<MapInstanceRegistry>({}, {
    key: "exploreMapInstances",
    autoSync: true
  });
  // Drop legacy numeric counters that could persist as stale state across sessions.
  clearSyncData("fleetMapMountedCount");
  clearSyncData("exploreMapMountedCount");
});

// Smooth playback time: advances continuously between selectedTime steps (rAF) when playing
const [smoothPlaybackTimeState, setSmoothPlaybackTimeState] = createSignal<Date>(new Date('1970-01-01T12:00:00Z'));
// Throttled copy for track/wind/bad air (~10fps) to avoid heavy recompute every frame
const TRACK_THROTTLE_MS = 100;
const [smoothPlaybackTimeForTrackState, setSmoothPlaybackTimeForTrackState] = createSignal<Date>(new Date('1970-01-01T12:00:00Z'));
let lastTrackUpdateMs = 0;
// Throttled copy for boat (~5fps) — glide toward playhead cap between WebSocket samples
const BOAT_THROTTLE_MS = 200;
const [smoothPlaybackTimeForBoatState, setSmoothPlaybackTimeForBoatState] = createSignal<Date>(new Date('1970-01-01T12:00:00Z'));
let lastBoatUpdateMs = 0;
/**
 * Publish cadence while a render-yield window is open (~2fps): the user is waiting on something (a
 * page load), so the animation gives the main thread back. Every animated map layer, chart and table
 * — live AND replay — reacts to the three smooth signals below, so throttling them here quietens all
 * of it without any consumer needing to know. See utils/renderYield.ts.
 */
const YIELD_PUBLISH_MS = 500;
let lastYieldPublishMs = 0;

let smoothRafId: number | null = null;
let lastAnchorTimeMs = 0;
let lastAnchorRealMs = 0;
let _pollingIntervalMs = 1000;
/** Wall time of the previous non-live playback tick (0 = none yet). */
let lastNonLiveTickRealMs = 0;
let prevSelectedTimeMs = 0;
/** Last smooth time we set; used to avoid boats/tracks jumping backwards when selectedTime syncs to an older value. */
let lastSmoothTimeMs = 0;
/** Wall-clock ms of previous rAF frame (live mode frame delta). */
let lastSmoothFrameMs = 0;
const DEFAULT_EPOCH_TIME_MS = new Date('1970-01-01T12:00:00Z').getTime();
const UNSET_PLAYBACK_TIME_MS = new Date('1971-01-01T12:00:00Z').getTime();

/** True when playback time was never initialized (default epoch or invalid). */
export const isUnsetPlaybackTime = (time: Date | null | undefined): boolean => {
  if (!time || !(time instanceof Date) || Number.isNaN(time.getTime())) return true;
  return time.getTime() < UNSET_PLAYBACK_TIME_MS;
};
/** Live mode: last display target cap — reset anchor when target jumps forward (new WS batch). */
let lastLiveDisplayTargetMs = 0;
/** Live mode: periodic selectedTime sync for cross-window / timeline labels. */
let lastLiveSelectedTimeSyncMs = 0;
const LIVE_SELECTED_TIME_SYNC_MS = 1000;

/**
 * Live smooth playhead step: advance toward cap, or hold at the data edge until cap moves forward.
 * Exported for unit tests.
 */
export function advanceLiveSmoothPlaybackMs(
  lastSmoothMs: number,
  capMs: number,
  frameDeltaMs: number,
  speed: number
): number {
  if (lastSmoothMs >= capMs - 1) {
    if (capMs > lastSmoothMs) {
      return Math.min(capMs, lastSmoothMs + frameDeltaMs * speed);
    }
    return lastSmoothMs;
  }
  return Math.min(capMs, lastSmoothMs + frameDeltaMs * speed);
}

/** A background tab throttles timers to ~1s. Don't turn that one late tick into a multi-second lurch. */
export const NON_LIVE_STEP_CAP_MS = 1000;

/**
 * Non-live playback step from the wall time since the previous tick.
 * A fixed 100ms step plays slower than real time whenever the main thread delays the interval
 * (a heavy explore/fleet map). 1x = realtime, 0.5x = half, 2x = twice — speed is not floored at 1,
 * so maneuver slow-motion stays behind wall time.
 */
export function nonLivePlaybackStepMs(elapsedRealMs: number, speed: number): number {
  if (typeof speed !== "number" || !(speed > 0)) return 0;
  const elapsed = Math.min(NON_LIVE_STEP_CAP_MS, Math.max(0, elapsedRealMs));
  return elapsed * speed;
}

/**
 * Timestamp of the newest live sample held for the selected sources, or null if nothing has arrived.
 *
 * The RAW data edge — no buffer, no delay, no wall-clock floor. `getLiveDisplayTargetMs` below is the
 * playhead TARGET derived from this, and is the wrong thing to measure staleness with: it falls back
 * to wall clock when there is no data at all, so a dead feed would keep reporting an age of zero.
 */
export function getLiveDataEdgeMs(sourceIds?: Set<number>): number | null {
  let ids = sourceIds ?? liveSourcesStore.selectedSourceIds();
  if (ids.size === 0) {
    ids = streamingStore.getSubscribedSourceIds();
  }
  if (ids.size === 0) { return null; }
  // GPU/columnar path: rings are keyed by the stream frame id, not the app source_id.
  // latestTs(ids) stays null while boats are already on the map. The feed edge is every ring
  // the server is streaming this session (same answer the playback interval uses).
  const latestMs = config.LIVE_MAP_GPU
    ? liveColumnarStore.latestTsAny()
    : streamingStore.getLatestTimestamp(ids);
  return latestMs != null && latestMs > 0 ? latestMs : null;
}

/**
 * Live replay playhead target: min(wall − buffer, latestData − dataDelay).
 * Exported for unit tests.
 */
export function getLiveDisplayTargetMs(sourceIds?: Set<number>): number {
  const bufferMsVal = liveConfigStore.bufferMs();
  const dataDelayMsVal = liveConfigStore.dataDelayMs();
  const wallTargetMs = Date.now() - bufferMsVal;
  const latestMs = getLiveDataEdgeMs(sourceIds);
  if (latestMs != null) {
    return Math.min(wallTargetMs, latestMs - dataDelayMsVal);
  }
  return wallTargetMs;
}

/** Ignore epoch / unset playhead until live data anchors the clock. */
export const MIN_LIVE_REPLAY_PLAYHEAD_MS = Date.UTC(2020, 0, 1);

export function isLiveReplayPlayheadReady(ms: number): boolean {
  return Number.isFinite(ms) && ms >= MIN_LIVE_REPLAY_PLAYHEAD_MS;
}

/** Snap smooth playback clocks to a live edge without waiting for the next rAF tick. */
export function snapLiveSmoothPlaybackToMs(ms: number): void {
  if (!isLiveReplayPlayheadReady(ms)) return;
  lastAnchorTimeMs = ms;
  prevSelectedTimeMs = ms;
  lastSmoothTimeMs = ms;
  lastLiveDisplayTargetMs = ms;
  lastAnchorRealMs = Date.now();
  lastSmoothFrameMs = 0;
  lastTrackUpdateMs = 0;
  lastBoatUpdateMs = 0;
  const date = new Date(ms);
  setSmoothPlaybackTimeState(date);
  setSmoothPlaybackTimeForTrackState(date);
  setSmoothPlaybackTimeForBoatState(date);
}

// When maneuver video reset is pressed, increment so chart Video tiles seek back to their fixedStartTime
const [maneuverVideoResetTriggerState, setManeuverVideoResetTriggerState] = createSignal(0);
export const maneuverVideoResetTrigger = maneuverVideoResetTriggerState;
export const setManeuverVideoResetTrigger = (fn: (prev: number) => number) => setManeuverVideoResetTriggerState(fn);

// Bumped when the timeseries playhead is dragged: chart Video tiles seek to their current effectiveTime
// (currentTimeForTile) rather than to clip start, so scrubbing moves the frame to the dragged time.
const [maneuverVideoSeekToCurrentTriggerState, setManeuverVideoSeekToCurrentTriggerState] = createSignal(0);
export const maneuverVideoSeekToCurrentTrigger = maneuverVideoSeekToCurrentTriggerState;
export const setManeuverVideoSeekToCurrentTrigger = (fn: (prev: number) => number) => setManeuverVideoSeekToCurrentTriggerState(fn);

// Configurable maneuver video start offset (seconds before maneuver time). Default -15; user can adjust via step controls when at init time.
const [maneuverVideoStartOffsetSecondsState, setManeuverVideoStartOffsetSecondsState] = createSignal(MANEUVER_VIDEO_START_OFFSET_SECONDS);
export const maneuverVideoStartOffsetSeconds = maneuverVideoStartOffsetSecondsState;
export const setManeuverVideoStartOffsetSeconds = (value: number | ((prev: number) => number)) => {
  if (typeof value === 'function') {
    setManeuverVideoStartOffsetSecondsState(value);
  } else {
    setManeuverVideoStartOffsetSecondsState(() => value);
  }
};

/** Maneuver-relative elapsed seconds (matches ManeuverPlayPause display counter). Timeseries playhead reads this during play. */
const [maneuverVideoElapsedSecondsState, setManeuverVideoElapsedSecondsState] = createSignal(MANEUVER_VIDEO_START_OFFSET_SECONDS);
export const maneuverVideoElapsedSeconds = maneuverVideoElapsedSecondsState;
export const setManeuverVideoElapsedSeconds = (value: number) => {
  setManeuverVideoElapsedSecondsState(value);
};

/**
 * Seek request from the maneuver-video timeseries playhead drag. ManeuverPlayPause owns the maneuver
 * datetime + display counter, so it consumes this and performs the actual seek (pause, set elapsed,
 * set selectedTime, re-seek tiles). `nonce` makes repeated same-seconds drags re-fire.
 */
const [maneuverVideoSeekRequestState, setManeuverVideoSeekRequestState] = createSignal<{ sec: number; nonce: number } | null>(null);
export const maneuverVideoSeekRequest = maneuverVideoSeekRequestState;
let maneuverVideoSeekNonce = 0;
export const requestManeuverVideoSeek = (sec: number) => {
  maneuverVideoSeekNonce += 1;
  setManeuverVideoSeekRequestState({ sec, nonce: maneuverVideoSeekNonce });
};

/**
 * Maneuver-relative seconds of the timeseries playhead, or null when the user hasn't scrubbed yet.
 * Nullable on purpose — unlike maneuverVideoElapsedSeconds (which starts at -15) this lets the
 * maneuver map tell "no playhead yet" from "playhead at clip start", so it draws no boat until asked.
 */
const [maneuverPlayheadSecondsState, setManeuverPlayheadSecondsState] = createSignal<number | null>(null);
export const maneuverPlayheadSeconds = maneuverPlayheadSecondsState;
export const setManeuverPlayheadSeconds = (sec: number | null) => setManeuverPlayheadSecondsState(sec);
export const clearManeuverPlayhead = () => setManeuverPlayheadSecondsState(null);

/** True when there is no document (Node/vitest) or the page is actually on screen. */
function isPageVisible(): boolean {
  if (typeof document === 'undefined') { return true; }
  return document.visibilityState !== 'hidden';
}

/**
 * Re-arm the smooth clock — the ONE place the loop decides how to continue.
 *
 * This was five bare `requestAnimationFrame(smoothPlaybackTick)` calls, and every one of them was a
 * permanent latch. The browser suspends rAF for a hidden page, and a callback queued before hiding
 * may never be delivered at all — so the loop died on the first focus loss and, because the only
 * thing that starts it is edge-triggered on `isPlaying`, nothing ever restarted it. Connected, data
 * arriving, transport reading LIVE, nothing moving, reload the only cure.
 *
 * Hidden pages stand the loop DOWN rather than queueing anything: rAF would not fire, and a timer
 * would only animate a page nobody is looking at. `recoverPlaybackClock` restarts it — on
 * `visibilitychange`, and on a plain interval, because an occluded or covered window never fires
 * visibilitychange at all. That third path is load-bearing, not belt-and-braces.
 *
 * Parked, not stopped: `smoothRafId == null` while `isPlaying` stays true is exactly what recovery
 * looks for.
 */
function scheduleSmoothTick(): void {
  if (!isPageVisible()) {
    smoothRafId = null;
    return;
  }
  smoothRafId = requestAnimationFrame(smoothPlaybackTick);
}

/** How long the smooth loop may go without a tick, while visible and playing, before it is presumed dead. */
const SMOOTH_TICK_STALL_MS = 2000;
/** Reconciliation cadence. Cheap: it reads two module vars and a signal, and no-ops while paused. */
const CLOCK_WATCHDOG_MS = 1000;
/** When the smooth loop last actually ran a frame. */
let lastSmoothTickMs = 0;
let clockWatchdogId: ReturnType<typeof setInterval> | null = null;
let playbackVisibilityListenerAttached = false;

/**
 * Restart whatever half of the clock has died, WITHOUT touching the transport.
 *
 * `initializeIsPlayingFollowEffect` is edge-triggered (`if (playing === lastObservedPlaying) return;`),
 * so anything that kills the machinery while `isPlaying` stays true is permanent — nothing re-runs.
 * That is why the freeze was permanent rather than transient.
 *
 * It is a reconciler, so the rules are strict: it may only ever START things, never stop them, and it
 * must NEVER write `isPlaying` or `liveFollow`. Writing either would resurrect a pause the user
 * asked for, which is the one thing that has to survive a hidden stretch untouched.
 *
 * `startSmoothPlayback` re-seeds from `getLiveDisplayTargetMs()` in live mode, so recovery snaps to
 * the data edge rather than crawling forward from a stale playhead.
 */
function recoverPlaybackClock(): void {
  // Hidden is a deliberate stand-down, not a fault — scheduleSmoothTick parked the loop on purpose.
  if (!isPageVisible()) { return; }
  if (!untrack(() => isPlayingState())) { return; }

  let repaired = false;
  if (untrack(() => playbackInterval()) == null && playbackRetryTimer == null) {
    // startPlaybackInterval starts the smooth loop itself, so this branch covers both halves.
    startPlaybackInterval();
    repaired = true;
  } else if (smoothRafId == null || Date.now() - lastSmoothTickMs > SMOOTH_TICK_STALL_MS) {
    startSmoothPlayback();
    repaired = true;
  }

  if (repaired) {
    // The stall backoff only observes the data edge from inside the playback interval, so while the
    // clock was dead nothing called observeEdge and it has been sitting at its maximum. Clearing it
    // means the first poll after recovery is immediate rather than up to 30s later.
    playbackPullBackoff.reset();
  }
}

function ensurePlaybackVisibilityListener(): void {
  if (playbackVisibilityListenerAttached) { return; }
  if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') { return; }
  playbackVisibilityListenerAttached = true;
  // Recover the moment the page is back, rather than up to one watchdog tick later.
  document.addEventListener('visibilitychange', () => {
    if (isPageVisible()) { recoverPlaybackClock(); }
  });
}

function startClockWatchdog(): void {
  ensurePlaybackVisibilityListener();
  if (clockWatchdogId != null) { return; }
  clockWatchdogId = setInterval(recoverPlaybackClock, CLOCK_WATCHDOG_MS);
}

function stopClockWatchdog(): void {
  if (clockWatchdogId != null) {
    clearInterval(clockWatchdogId);
    clockWatchdogId = null;
  }
}

function smoothPlaybackTick(): void {
  const playing = isPlayingState();
  if (!playing) {
    smoothRafId = null;
    lastSmoothFrameMs = 0;
    return;
  }
  const now = Date.now();
  lastSmoothTickMs = now;
  let frameDelta = lastSmoothFrameMs > 0 ? now - lastSmoothFrameMs : 0;
  if (frameDelta > 200) frameDelta = 16;
  lastSmoothFrameMs = now;

  const isLive = liveModeState();
  const speed = typeof playbackSpeedState() === "number" && playbackSpeedState() > 0 ? playbackSpeedState() : 1;

  let capMs: number;
  let candidateMs: number;

  if (isLive) {
    capMs = getLiveDisplayTargetMs();
    if (lastSmoothTimeMs === 0 || lastSmoothTimeMs <= DEFAULT_EPOCH_TIME_MS + 86400000) {
      lastSmoothTimeMs = capMs;
    }
    candidateMs = advanceLiveSmoothPlaybackMs(lastSmoothTimeMs, capMs, frameDelta, speed);
    lastLiveDisplayTargetMs = capMs;
  } else {
    const current = selectedTime();
    const currentMs = current instanceof Date ? current.getTime() : 0;
    if (currentMs === 0) {
      scheduleSmoothTick();
      return;
    }
    if (prevSelectedTimeMs !== currentMs) {
      lastAnchorTimeMs = prevSelectedTimeMs || currentMs;
      lastAnchorRealMs = now;
      prevSelectedTimeMs = currentMs;
    }
    capMs = currentMs;
    const elapsed = now - lastAnchorRealMs;
    candidateMs = Math.min(capMs, lastAnchorTimeMs + elapsed * speed);
  }

  // Never move smooth time backwards (e.g. when selectedTime syncs from another tab or a history refetch)
  const smoothMs = candidateMs >= lastSmoothTimeMs ? candidateMs : lastSmoothTimeMs;
  lastSmoothTimeMs = smoothMs;

  // Interaction takes priority: while a yield window is open, publish at ~2fps instead of frame rate.
  // The clock itself keeps advancing (and the rAF loop keeps running), so when the window closes the
  // playhead reflects real elapsed time — the never-move-backwards guard above covers the resume.
  if (isRenderYieldActive()) {
    if (now - lastYieldPublishMs >= YIELD_PUBLISH_MS) {
      lastYieldPublishMs = now;
      lastTrackUpdateMs = now;
      lastBoatUpdateMs = now;
      const yieldedTime = new Date(smoothMs);
      setSmoothPlaybackTimeState(yieldedTime);
      setSmoothPlaybackTimeForTrackState(yieldedTime);
      setSmoothPlaybackTimeForBoatState(yieldedTime);
    }
    scheduleSmoothTick();
    return;
  }

  setSmoothPlaybackTimeState(new Date(smoothMs));
  // Throttle updates for track/wind/bad air so they don't recompute 60 times/sec
  if (now - lastTrackUpdateMs >= TRACK_THROTTLE_MS) {
    lastTrackUpdateMs = now;
    setSmoothPlaybackTimeForTrackState(new Date(smoothMs));
  }
  // Throttle boat updates (~5fps); short transition fills in gaps for smooth look
  if (now - lastBoatUpdateMs >= BOAT_THROTTLE_MS) {
    lastBoatUpdateMs = now;
    setSmoothPlaybackTimeForBoatState(new Date(smoothMs));
  }
  scheduleSmoothTick();
}

function startSmoothPlayback(): void {
  const isLive = liveModeState();
  let ms: number;
  if (isLive) {
    ms = getLiveDisplayTargetMs();
    lastLiveDisplayTargetMs = ms;
  } else {
    const t = selectedTime();
    ms = t instanceof Date ? t.getTime() : 0;
  }
  if (ms > 0) {
    lastAnchorTimeMs = ms;
    prevSelectedTimeMs = ms;
    lastSmoothTimeMs = ms;
    const date = new Date(ms);
    setSmoothPlaybackTimeState(date);
    setSmoothPlaybackTimeForTrackState(date);
    setSmoothPlaybackTimeForBoatState(date);
  }
  lastAnchorRealMs = Date.now();
  lastSmoothFrameMs = 0;
  lastTrackUpdateMs = 0;
  lastBoatUpdateMs = 0;
  if (smoothRafId != null) cancelAnimationFrame(smoothRafId);
  scheduleSmoothTick();
}

function stopSmoothPlayback(): void {
  if (smoothRafId != null) {
    cancelAnimationFrame(smoothRafId);
    smoothRafId = null;
  }
  const t = selectedTime();
  const date = t instanceof Date ? t : new Date('1970-01-01T12:00:00Z');
  lastSmoothTimeMs = date.getTime();
  setSmoothPlaybackTimeState(date);
  setSmoothPlaybackTimeForTrackState(date);
  setSmoothPlaybackTimeForBoatState(date);
}

export function smoothPlaybackTime(): Date {
  if (!isPlayingState()) return selectedTime();
  const t = smoothPlaybackTimeState();
  return t instanceof Date && !isNaN(t.getTime()) ? t : selectedTime();
}

/** Throttled (~10fps) for track, wind, bad air - use this in MapContainer effectivePlaybackTime to avoid 60fps recompute. */
export function smoothPlaybackTimeForTrack(): Date {
  if (!isPlayingState()) return selectedTime();
  const t = smoothPlaybackTimeForTrackState();
  return t instanceof Date && !isNaN(t.getTime()) ? t : selectedTime();
}

/** Throttled (~5fps) for boat — interpolate position/heading toward live display cap. */
export function smoothPlaybackTimeForBoat(): Date {
  if (!isPlayingState()) return selectedTime();
  const t = smoothPlaybackTimeForBoatState();
  return t instanceof Date && !isNaN(t.getTime()) ? t : selectedTime();
}

// Note: We'll add logging in the selectedTime getter instead to avoid createEffect issues

export const selectedTime = () => {
  const time = selectedTimeState() as any;
  
  // Handle both Date objects and string values (from cross-window sync)
  let dateTime: Date;
  
  if (time instanceof Date && !isNaN(time.getTime())) {
    dateTime = time;
  } else if (typeof time === 'string' && time) {
    // Convert string to Date (from cross-window sync)
    dateTime = new Date(time);
    if (isNaN(dateTime.getTime())) {
      debug(`🔄 selectedTime: Invalid string date, using default:`, time);
      dateTime = new Date('1970-01-01T12:00:00Z');
    }
    // Only log conversion if it's not a frequent repeated value (reduce noise)
    // Removed excessive logging for normal string-to-Date conversions
  } else {
    debug(`🔄 selectedTime: Invalid time value, using default:`, { time, type: typeof time });
    dateTime = new Date('1970-01-01T12:00:00Z');
  }
  
  // Only log when selectedTime is accessed and it's the default value (reduce noise)
  // Removed default value logging as it's too verbose
  
  return dateTime;
};

// Add throttling for selectedTime updates
let lastSelectedTimeSync: number = 0;
let lastSelectedTimeValue: Date | null = null;

export const setSelectedTime = (value: Date, requestingComponent?: string, force?: boolean) => {
  if (value instanceof Date) {
    // Reject invalid dates (e.g. from scale.invert() when chart is not yet laid out or in small/split view)
    const timeMs = value.getTime();
    if (!Number.isFinite(timeMs)) {
      debug(`⏰ setSelectedTime: Ignoring invalid date (e.g. from invert in small/split view)`, { requestingComponent });
      return;
    }
    // During playback, only the playback store may set selectedTime (single source of truth)
    if (isPlayingState() && requestingComponent !== undefined && requestingComponent !== 'playback') {
      return;
    }
    // Check if component has permission to set selectedTime
    const currentActiveName = getActiveComponentName();
    if (requestingComponent && currentActiveName && currentActiveName !== requestingComponent) {
      const currentPriority = COMPONENT_PRIORITIES[currentActiveName] || 0;
      const requestedPriority = COMPONENT_PRIORITIES[requestingComponent] || 0;
      const livePlaybackPreempt =
        requestingComponent === 'playback' && canPlaybackPreemptInLiveMode(currentActiveName);

      if (!livePlaybackPreempt && requestedPriority < currentPriority) {
        debug(`🚫 setSelectedTime denied for '${requestingComponent}' - '${activeComponent()}' has higher priority (${currentPriority} > ${requestedPriority})`);
        return;
      }

      if (livePlaybackPreempt) {
        setActiveComponent('playback');
      }
      
      // If user interaction takes control from playback, pause playback
      if (currentActiveName === 'playback' && (requestingComponent === 'maptimeseries' || requestingComponent === 'timeseries' || requestingComponent === 'map')) {
        debug(`⏸️ User interaction taking control from playback - pausing playback`);
        setIsPlaying(false);
        clearPlaybackInterval();
        // Do NOT auto-restart playback after manual time change
      }
      
      // Also pause playback if this is a manual time change (regardless of component)
      if (currentActiveName === 'playback' && requestingComponent && requestingComponent !== 'playback') {
        debug(`⏸️ Manual time change detected - pausing playback`);
        setIsPlaying(false);
        clearPlaybackInterval();
        // Do NOT auto-restart playback after manual time change
      }
    }
    
    const defaultTime = new Date('1970-01-01T12:00:00Z');
    const _isDefaultTime = value.getTime() === defaultTime.getTime();
    
    // Only log setSelectedTime for non-playback components or significant changes
    if (requestingComponent && requestingComponent !== 'playback') {
      debug(`⏰ setSelectedTime by '${requestingComponent}':`, value.toISOString());
    }
    
    if (!force && lastSelectedTimeValue && Math.abs(value.getTime() - lastSelectedTimeValue.getTime()) < 20) {
      return; // Skip if time hasn't changed significantly (reduced to 20ms for very smooth animation)
    }

    if (requestingComponent === 'playback' && lastSelectedTimeValue) {
      lastAnchorTimeMs = lastSelectedTimeValue.getTime();
      lastAnchorRealMs = Date.now();
    }

    // When user clicks to set time on map/timeseries, pause playback so all windows stay in sync
    const manualTimeComponents = ['maptimeseries', 'timeseries', 'multimaptimeseries', 'livemaptimeseries', 'map'];
    if (requestingComponent && requestingComponent !== 'playback' && manualTimeComponents.includes(requestingComponent) && isPlayingState()) {
      setIsPlayingState(false);
      safeSync(syncIsPlaying, 'syncIsPlaying');
    }

    setSelectedTimeState(value);
    lastSelectedTimeValue = value;

    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('playbackSelectedTimeChange', {
            detail: { selectedTime: value, requestingComponent },
          })
        );
      }
    } catch (_) { /* empty */ }

    // Reset manual change flag after a short delay to allow components to react
    setTimeout(() => {
      setIsManualTimeChange(false);
    }, 50);
    
    // Do not auto-restart playback after manual time changes
    
    // Much more aggressive throttling for sync calls
    const now = Date.now();
    if (now - lastSelectedTimeSync > 500) { // Only sync every 500ms
      lastSelectedTimeSync = now;
      if (typeof syncSelectedTime === "function") {
        safeSync(syncSelectedTime, 'syncSelectedTime');
        // Removed frequent sync log - only syncs every 500ms anyway
      }
    }
    
    // Broadcast to parent window for cross-window sync
    try {
      if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
        debug(`📡 Broadcasting selectedTime to parent window:`, value.toISOString());
        window.opener.postMessage({
          type: 'PLAYBACK_UPDATE_FROM_CHILD',
          payload: {
            type: 'TIME_CHANGE',
            selectedTime: value.toISOString(),
            requestingComponent
          },
          windowName: window.name
        }, window.location.origin);
      }
    } catch (error) {
      warn('Error broadcasting selectedTime update:', error);
    }
  } else {
    warn("Invalid value for selectedTime. Expected a Date object.", value);
  }
};

// Export the sync function for manual synchronization
export const syncSelectedTimeManual: SyncFunction = () => {
  safeSync(syncSelectedTime, 'syncSelectedTime');
};

// Force sync all playback signals when needed
export const forceSyncAll = () => {
  debug('⏰ PlaybackStore: Force syncing all signals');
  safeSync(syncSelectedTime, 'syncSelectedTime');
  safeSync(syncIsPlaying, 'syncIsPlaying');
  safeSync(syncPlaybackSpeed, 'syncPlaybackSpeed');
  safeSync(syncTimeWindow, 'syncTimeWindow');
  safeSync(syncShowPlayback, 'syncShowPlayback');
  safeSync(syncVideoTime, 'syncVideoTime');
  safeSync(syncIsManualTimeChange, 'syncIsManualTimeChange');
};

// Auto-hide playback controls when selections are made, without changing the user's time window.
let lastSelectionState = false;
let selectionEffectInitialized = false;
let selectionEffectDispose: (() => void) | null = null;

export const initializeSelectionEffect = () => {
  if (selectionEffectInitialized) return;
  selectionEffectInitialized = true;
  
  if (selectionEffectDispose) {
    selectionEffectDispose();
  }
  
  selectionEffectDispose = createRoot((dispose) => {
    createEffect(() => {
      try {
        // Check if there are active selections
        const hasSelections = selectedEvents().length > 0 || selectedRange().length > 0;
        
        // Only update if the selection state has actually changed
        if (hasSelections !== lastSelectionState) {
          lastSelectionState = hasSelections;
          
          if (hasSelections) {
            setShowPlayback(false);
          } else {
            setShowPlayback(true);
          }
        }
      } catch (error) {
        // Ignore errors if selection store is not available
      }
    });
    
    return dispose;
  });
};

export const isPlaying = () => {
  const playing = isPlayingState();
  return typeof playing === "boolean" ? playing : false;
};

// Add a guard to prevent rapid toggling
let lastSetIsPlayingTime = 0;
const MIN_SET_INTERVAL = 50; // Minimum 50ms between setIsPlaying calls

/** Child /window startup pauses locally; must not pause the opener's live map. */
let suppressParentPlaybackBroadcast = 0;

export function runWithoutParentPlaybackBroadcast(fn: () => void): void {
  suppressParentPlaybackBroadcast++;
  try {
    fn();
  } finally {
    suppressParentPlaybackBroadcast--;
  }
}

export interface SetIsPlayingOptions {
  /**
   * Bypass the anti-toggle throttle below.
   *
   * Only a deliberate transport gesture may pass this. A resume silently swallowed by the throttle is
   * what leaves the Follow button reading LIVE over a stopped clock, so `setLivePlaying` — which is
   * only ever reached from a user click — always forces.
   */
  force?: boolean;
}

export const setIsPlaying = (value: boolean, options?: SetIsPlayingOptions) => {
  const now = Date.now();
  const timeSinceLastCall = now - lastSetIsPlayingTime;
  
  // Prevent rapid toggling for play; always allow pause so cross-window sync is reliable
  if (value === true && options?.force !== true && timeSinceLastCall < MIN_SET_INTERVAL) {
    debug(`[PlaybackStore] Throttling setIsPlaying call (${timeSinceLastCall}ms since last call)`);
    return;
  }
  
  lastSetIsPlayingTime = now;

  // Clear brush when play is pressed, but not when event-based selection is active (e.g. single-source Maneuvers VIDEO with rows selected).
  // In split view, do NOT clear brush on play: the other panel (e.g. maneuvers) may trigger play/sync and would wipe the map panel's timeseries brush selection.
  const inSplitView = typeof document !== 'undefined' && document.querySelector('.split-view-content') != null;
  if (value === true && selectedEvents().length === 0 && !inSplitView) {
    setSelectedRange([]);
    // Also clear the visual brush on the timeseries chart
    if (typeof window !== 'undefined') {
      if (window.clearMapBrush) {
        try {
          window.clearMapBrush();
        } catch (err) {
          debug(`[PlaybackStore] Error clearing map brush:`, err);
        }
      }
      if (window.clearTimeSeriesBrush) {
        try {
          window.clearTimeSeriesBrush();
        } catch (err) {
          debug(`[PlaybackStore] Error clearing timeseries brush:`, err);
        }
      }
    }
  }
  
  setIsPlayingState(value);
  
  // Immediate sync like in the example (skip during child /window startup so opener stays playing)
  if (suppressParentPlaybackBroadcast === 0) {
    safeSync(syncIsPlaying, 'syncIsPlaying');
  }

  // Notify parent so it can forward to other child windows (cross-window pause/play sync)
  try {
    if (
      suppressParentPlaybackBroadcast === 0 &&
      typeof window !== 'undefined' &&
      window.opener &&
      !window.opener.closed
    ) {
      window.opener.postMessage({
        type: 'PLAYBACK_UPDATE_FROM_CHILD',
        payload: { type: 'IS_PLAYING_CHANGE', isPlaying: value },
        windowName: window.name
      }, window.location.origin);
    }
  } catch (err) {
    warn('Error broadcasting isPlaying to parent window:', err);
  }

  if (value === true) {
    // Clear manual time change flag so the manual-time-change effect doesn't immediately pause
    // (e.g. user clicked timeline earlier then pressed play — playback should stay playing)
    setIsManualTimeChange(false);

    // If a manual component currently holds control but no manual interaction is active,
    // release it so playback can take over smoothly
    const currentActive = getActiveComponentName();
    if (currentActive && (currentActive === 'map' || currentActive === 'timeseries' || currentActive === 'maptimeseries')) {
      try {
        if (!getIsManual()) {
          debug(`[PlaybackStore] Releasing manual component '${currentActive}' before starting playback`);
          releaseTimeControl(currentActive);
        }
      } catch (e) {
        // ignore
      }
    }
    // Interval start/stop is handled only by initializeIsPlayingFollowEffect to avoid double-call and races.
  }
};

// (moved watcher below, after playbackInterval signal initialization)

export const playbackSpeed = () => {
  const speed = playbackSpeedState();
  return typeof speed === "number" ? speed : 1; // Default to normal speed
};

export const setPlaybackSpeed = (value: number) => {
  setPlaybackSpeedState(Math.round(value));
  // Immediate sync
  safeSync(syncPlaybackSpeed, 'syncPlaybackSpeed');
  // Apply new speed immediately if currently playing, without relying on a root-less effect
  const speed = playbackSpeed();
  if (typeof speed === 'number' && speed > 0 && isPlaying()) {
    clearPlaybackInterval();
    startPlaybackInterval();
  }
};

export const timeWindow = () => {
  const window = timeWindowState();
  // Coerce to number (sync may return string from storage); treat invalid as 0 = full window
  let value = Number(window);
  if (!Number.isFinite(value) || value < 0) value = 0;

  // In live mode, cap the time window at 30 minutes (even if stored value is higher)
  // Use untrack to prevent reactive loops - we only want to cap based on current liveMode state
  if (untrack(() => liveMode()) && value > 30) {
    value = 30;
  }

  return value;
};

/** Map timeline brush is active (not event-table selection). */
export const isMapBrushSelectionActive = (): boolean => {
  try {
    const ranges = selectedRange();
    return Array.isArray(ranges) && ranges.length > 0;
  } catch {
    return false;
  }
};

/** Default epoch playhead before fleet/explore timeline init — not a real data time. */
export const DEFAULT_PLAYBACK_EPOCH_MS = new Date('1970-01-01T12:00:00Z').getTime();

export const isDefaultPlaybackTime = (t?: Date | null): boolean => {
  if (!t || !(t instanceof Date) || isNaN(t.getTime())) {
    return true;
  }
  return t.getTime() === DEFAULT_PLAYBACK_EPOCH_MS;
};

/**
 * Whether the playback time window (minutes) should filter map tracks / drive the narrow chart window.
 * Suspended while a map brush is active so brushing behaves like "full" mode; restored when brush clears.
 * Also suspended until the playhead leaves the default epoch so first paint shows full tracks.
 */
export const shouldApplyPlaybackTimeWindow = (): boolean => {
  const tw = timeWindow();
  if (!Number.isFinite(tw) || tw <= 0) {
    return false;
  }
  if (isMapBrushSelectionActive()) {
    return false;
  }
  if (isCut() && Array.isArray(cutEvents()) && cutEvents().length > 0) {
    return false;
  }
  if (isDefaultPlaybackTime(selectedTime())) {
    return false;
  }
  return true;
};

/**
 * Single source of truth for the reference time used for time-window display.
 * When playing, use smooth time so chart and map stay in sync; when paused use selectedTime.
 */
export const getDisplayWindowReferenceTime = (): Date | null => {
  const res = isPlayingState()
    ? (() => {
        // Live map: BSP table and timeline follow the boat playhead (~5fps), not the 10fps track clock
        const t = liveModeState()
          ? smoothPlaybackTimeForBoatState()
          : smoothPlaybackTimeForTrackState();
        return t instanceof Date && !isNaN(t.getTime()) ? t : selectedTime();
      })()
    : selectedTime();
  return res;
};

/** Live FleetMap instances across windows (heartbeat registry; stale entries expire). */
export const fleetMapMountedCount = (): number => fleetMapInstanceController.count();

/**
 * Register a live FleetMap mount. Returns cleanup — call on component unmount.
 * Heartbeats keep cross-window presence fresh; entries expire without heartbeat.
 */
export const registerFleetMapInstance = (): (() => void) => fleetMapInstanceController.register();

/** @deprecated Use registerFleetMapInstance — kept for callers not yet migrated. */
export const incrementFleetMapMounted = (): void => fleetMapInstanceController.increment();

/** @deprecated Use registerFleetMapInstance cleanup — kept for callers not yet migrated. */
export const decrementFleetMapMounted = (): void => fleetMapInstanceController.decrement();

/** Live dataset explore map instances across windows (heartbeat registry; stale entries expire). */
export const exploreMapMountedCount = (): number => exploreMapInstanceController.count();

/**
 * Register a live dataset explore map mount. Returns cleanup — call on component unmount.
 * Heartbeats keep cross-window presence fresh; entries expire without heartbeat.
 */
export const registerExploreMapInstance = (): (() => void) => exploreMapInstanceController.register();

/** @deprecated Use registerExploreMapInstance — kept for callers not yet migrated. */
export const incrementExploreMapMounted = (): void => exploreMapInstanceController.increment();

/** @deprecated Use registerExploreMapInstance cleanup — kept for callers not yet migrated. */
export const decrementExploreMapMounted = (): void => exploreMapInstanceController.decrement();

export const setTimeWindow = (value: number) => {
  // Round to 1 decimal place to preserve 0.5 (30 seconds) while handling floating point precision
  let finalValue = Math.round(value * 10) / 10;
  
  // In live mode, cap the time window at 30 minutes
  // Use untrack to prevent reactive loops
  if (untrack(() => liveMode()) && finalValue > 30) {
    finalValue = 30;
  }
  
  setTimeWindowState(finalValue);
  // Immediate sync
  safeSync(syncTimeWindow, 'syncTimeWindow');
};

export const videoTime = () => {
  const time = videoTimeState() as any;
  return time instanceof Date ? time : null;
};

export const setVideoTime = (value: Date | null) => {
  if (value instanceof Date || value === null) {
    setVideoTimeState(value);
    safeSync(syncVideoTime, 'syncVideoTime');
  } else {
    warn("Invalid value for videoTime. Expected a Date object or null.", value);
  }
};

// Export the sync function for manual synchronization
export const syncVideoTimeManual: SyncFunction = () => {
  safeSync(syncVideoTime, 'syncVideoTime');
};

export const playbackInterval = () => {
  const interval = playbackIntervalState();
  return typeof interval === "number" ? interval : null; // Ensure a number or null
};

export const setPlaybackInterval = (value: number | null) => {
  if (value !== null && typeof value !== "number") {
    warn("Invalid interval ID. Expected a number or null, received:", value);
    return; // Reject invalid values
  }
  setPlaybackIntervalState(value);
};

// Ensure playback interval follows isPlaying even when updated via cross-window sync
// Create this effect lazily from a component root to avoid disposal warnings
let isPlayingFollowEffectInitialized = false;
let isPlayingFollowEffectDispose: (() => void) | null = null;
export const initializeIsPlayingFollowEffect = () => {
  if (isPlayingFollowEffectInitialized) return;
  isPlayingFollowEffectInitialized = true;
  
  if (isPlayingFollowEffectDispose) {
    isPlayingFollowEffectDispose();
  }
  
  isPlayingFollowEffectDispose = createRoot((dispose) => {
    let lastObservedPlaying: boolean | null = null;
    createEffect(() => {
      const playing = isPlaying();
      if (playing === lastObservedPlaying) return;
      lastObservedPlaying = playing;
      if (playing) {
        startPlaybackInterval();
      } else {
        clearPlaybackInterval();
      }
    });
    
    return dispose;
  });
};

export const isManualTimeChange = isManualTimeChangeState;
export const setIsManualTimeChange = (value: boolean) => {
  // Only log when value changes
  if (isManualTimeChangeState() !== value) {
    debug('setIsManualTimeChange:', value);
  }
  setIsManualTimeChangeState(value);
  safeSync(syncIsManualTimeChange, 'syncIsManualTimeChange');
};

/** One-shot hint for MapContainer to center on a boat when Tags explore seeks time. */
export type TagsSeekMapCenterHint = { sourceId?: number | null };
let tagsSeekMapCenterHint: TagsSeekMapCenterHint | null = null;

export function setTagsSeekMapCenter(hint?: TagsSeekMapCenterHint): void {
  tagsSeekMapCenterHint = hint ?? {};
}

export function takeTagsSeekMapCenter(): TagsSeekMapCenterHint | null {
  const hint = tagsSeekMapCenterHint;
  tagsSeekMapCenterHint = null;
  return hint;
}

export const shouldRestartPlayback = shouldRestartPlaybackState;
export const setShouldRestartPlayback = (value: boolean) => {
  setShouldRestartPlaybackState(value);
};

// Playback-related signals
export const showPlayback = () => showPlaybackState() ?? true;

export const setShowPlayback = (value: boolean) => {
  setShowPlaybackState(value);
  // Ensure sync after state change
  setTimeout(() => safeSync(syncShowPlayback, 'syncShowPlayback'), 0);
};

export const activeComponent = () => activeComponentState();
export const setActiveComponent = (component: string | null) => {
  // Avoid redundant updates that can cause cross-window sync loops
  try {
    const current = (activeComponentState() as unknown) as string | null;
    if (current === component) {
      return;
    }
  } catch {
    // ignore getter errors and proceed with set
  }
  setActiveComponentState(component);
  safeSync(syncActiveComponent, 'syncActiveComponent');
};

export const liveMode = () => liveModeState() ?? false;
const DEFAULT_EPOCH_TIME = DEFAULT_EPOCH_TIME_MS;
export const setLiveMode = (value: boolean) => {
  // untrack the reads: callers include tracking scopes (LiveMapContainer runs setLiveMode(true)
  // inside a createEffect), and a tracked read of selectedTime there re-runs the caller on every
  // clock tick / cross-window sync — which used to re-arm follow below and clobber a paused
  // user's selection on the live Performance & Maneuvers pages.
  const wasLive = Boolean(untrack(liveMode));
  setLiveModeState(value);
  // When entering live mode, set time to now (UTC) if still at default/uninitialized
  if (value) {
    const current = untrack(selectedTime);
    if (!current || current.getTime() === DEFAULT_EPOCH_TIME || current.getTime() < Date.now() - 24 * 60 * 60 * 1000) {
      setSelectedTimeState(new Date());
      debug('⏰ PlaybackStore: Live mode on - set selectedTime to now (UTC)');
    }
    // Follow the live edge by default when *entering* live mode (auto-select latest).
    // Already live: leave follow alone — a repeat setLiveMode(true) must never undo a manual pause.
    if (!wasLive) {
      setLiveFollowState(true);
    }
  } else if (wasLive) {
    // Leaving live mode: stop following so non-live pages behave normally.
    setLiveFollowState(false);
  }
};

/** "Follow the live edge / auto-select latest" master switch (synced cross-window). */
export const liveFollow = () => liveFollowState() ?? false;
export const setLiveFollow = (value: boolean) => {
  setLiveFollowState(value);
};
/** Follow is only meaningful in live mode. */
export const liveFollowActive = () => (liveModeState() ?? false) && (liveFollowState() ?? false);

// -------------------------------------------------------------------------------------------------
// Live REPORT follow — per-page switches for the pages that ACCUMULATE rows.
//
// `liveFollow` above and `isPlaying` are one thing in two halves: the CLOCK. They move together so
// the live map, the map timeline and the live Time Series — all moving-window surfaces — freeze and
// resume as one.
//
// Live Maneuvers, Live Performance and Live Fleet Summary are not moving-window surfaces. They
// accumulate: new maneuvers / aggregates / debrief rows merge into a table that only grows. Pausing
// one of those pages means "stop moving things under me while I pick some events", and that has
// nothing to do with whether the boat on the map is still advancing — OR whether a sibling report
// page should keep following. Sharing one report signal made Maneuvers and Performance cross: pause
// on either paused both.
//
// So each report page gets its OWN switch. Nothing here touches the clock, and the clock never
// touches these. Pausing Maneuvers leaves Performance following, and vice versa.
//
// Deliberately PLAIN LOCAL SIGNALs, not `createSyncSignal`. "I am selecting events in this window"
// is per-window intent by definition, and a popped-out Live Maneuvers must not be able to pause a
// report in the parent. It also keeps this out of the cross-window write path that made `liveMode`
// and `liveFollow` flip under the user.
//
// NOTHING may write these except the user: the <LiveFollowToggle/> on that page, its select-to-pause
// / brush-to-pause, and nothing else. Not a stalled feed, not teardown, not `setLiveMode`. Same rule
// as `liveFollow`, for the same reason — it means intent.
// -------------------------------------------------------------------------------------------------
const [liveManeuverFollowState, setLiveManeuverFollowState] = createSignal<boolean>(true);
const [livePerformanceFollowState, setLivePerformanceFollowState] = createSignal<boolean>(true);
const [liveSummaryFollowState, setLiveSummaryFollowState] = createSignal<boolean>(true);

/** "Keep merging new rows into the live Maneuvers table." */
export const liveManeuverFollow = (): boolean => liveManeuverFollowState();
export const setLiveManeuverFollow = (value: boolean): void => {
  setLiveManeuverFollowState(value);
};
/** Maneuver report follow is only meaningful in live mode. */
export const liveManeuverFollowActive = (): boolean =>
  (liveModeState() ?? false) && liveManeuverFollowState();

/** "Keep merging new rows into the live Performance tables." */
export const livePerformanceFollow = (): boolean => livePerformanceFollowState();
export const setLivePerformanceFollow = (value: boolean): void => {
  setLivePerformanceFollowState(value);
};
/** Performance report follow is only meaningful in live mode. */
export const livePerformanceFollowActive = (): boolean =>
  (liveModeState() ?? false) && livePerformanceFollowState();

/**
 * "Keep the live Fleet Summary trailing window advancing."
 *
 * Deliberately NOT gated on `liveMode`: this page never writes liveMode (the live MAP container
 * does), so an Active helper would read false over a Follow toggle that still says LIVE.
 */
export const liveSummaryFollow = (): boolean => liveSummaryFollowState();
export const setLiveSummaryFollow = (value: boolean): void => {
  setLiveSummaryFollowState(value);
};

// -------------------------------------------------------------------------------------------------
// Live transport — ONE client-side play/pause for every moving-window live surface.
//
// Two signals carry it, and both are needed:
//   - `isPlaying`  drives the CLOCK (playback interval + smooth rAF) and everything the live map and
//                  its timeline already gate on. It cannot express "the user paused" on its own: the
//                  live tick auto-resumes it whenever the playhead sits near the data edge.
//   - `liveFollow` is the live-specific "chase the edge" state. Nothing auto-re-arms it, so it is what
//                  a live transport control can honestly display.
//
// THE TRANSPORT MOVES ONLY WHEN THE USER MOVES IT. `liveFollow` means intent, so nothing else may
// write it — not a stalled feed, not teardown, not a zoom hold, not a chart reinitialising. Those
// call `stopPlaybackClock()` instead, which stops the clock and leaves follow armed.
//
// A frozen feed is REPORTED, not acted on: the transport keeps reading LIVE and the status pill shows
// STALE with the age of the newest sample. Alinghi learned this the hard way — it had an effect that
// mirrored every clock pause into follow unless the caller remembered to set an opt-out flag, and an
// upstream stall then surfaced as PAUSED on a live map nobody had touched. Opt-out is the wrong
// polarity for a signal that means intent; the safe default is "this was not the user".
// -------------------------------------------------------------------------------------------------

/**
 * True while the live surfaces should be advancing — what a live transport control displays.
 *
 * Deliberately `liveFollow` and not `isPlaying`: follow is the user's INTENT (nothing re-arms it
 * behind their back) whereas the clock is machinery that startup, teardown and transient chart work
 * stop and restart on their own.
 */
export const livePlaying = (): boolean => liveFollow();

/**
 * The only way live UI should start/stop playback. Writes both halves of the transport, so pausing on
 * one moving-window live surface freezes all of them.
 */
export const setLivePlaying = (value: boolean): void => {
  setLiveFollow(value);
  if (value) {
    // A scrub taken while paused leaves the manual hold set, and playback can never take the clock
    // back while it is. Resuming has to clear it or the clock stays stopped with the button reading
    // "playing".
    setIsManualTimeChange(false);

    // Snap `selectedTime` to the live edge before restarting the clock.
    //
    // Without this, resuming after a scrub-back of a few seconds never catches up: the live tick
    // advances selectedTime one second per second of wall clock, so the gap is preserved forever,
    // while startSmoothPlayback re-seeds the SMOOTH clock straight from the data edge. The boats jump
    // to the edge and the transport's clock stays permanently behind them.
    //
    // Done while isPlaying is still false, deliberately: the manual-time-change re-pause in
    // setSelectedTime only bites while playing.
    // `isLiveReplayPlayheadReady` is this store's own readiness rule (an epoch/unset playhead means
    // live data has not anchored the clock yet) — snapping to one of those would drag the transport
    // back to 1970.
    const edgeMs = getLiveDisplayTargetMs();
    if (isLiveReplayPlayheadReady(edgeMs)) {
      setSelectedTime(new Date(edgeMs), 'playback');
    }
  }
  // force: this is a deliberate transport click. A throttled resume here is exactly what leaves the
  // control reading LIVE with a stopped clock.
  setIsPlaying(value, { force: true });
};

/**
 * Jump a live surface to `ms` and HOLD it there — the one sanctioned "pause and seek" for live pages.
 *
 * Every live seek has to do the same four things, and every one is load-bearing:
 *
 *  1. Drop `liveFollow`. A bare `setIsPlaying(false)` cannot express a user pause in live mode: the
 *     live tick auto-resumes the clock whenever the playhead sits near the data edge, so within ~2s
 *     the seek is undone and the map snaps back to the live edge. Only follow holds a pause.
 *  2. Stop the clock, BEFORE step 4. `setSelectedTime` refuses any write from a component other
 *     than 'playback' while `isPlaying`, so seeking first is a silent no-op.
 *  3. Take time control, BEFORE step 4. Live maps hold 'playback' (priority 2) for the whole
 *     session, so a caller that does not out-rank it is denied — which is why alarm clicks, at an
 *     unlisted priority of 0, did nothing at all.
 *  4. Set the time and snap the smooth clocks. Without the snap the rAF clocks keep reporting the
 *     pre-seek position and the boats sit where they were until the next tick.
 *
 * Steps 2 and 3 must precede step 4; 1 is order-independent (nothing ticks mid-function) but must
 * happen. `playbackStore.seekLive.test.ts` pins each of those three claims by mutation.
 *
 * Control is released on a later tick, not immediately: the seek dispatches a
 * `playbackSelectedTimeChange` event that listeners act on, and releasing in the same turn lets a
 * still-running live effect reclaim control and stomp the seek before they run.
 */
export const seekLive = (ms: number, component: string): void => {
  if (!Number.isFinite(ms)) return;

  setLiveFollow(false);
  setIsPlaying(false);

  const granted = requestTimeControl(component);
  if (!granted) {
    debug(`[PlaybackStore] seekLive denied for '${component}' (active: ${getActiveComponentName()})`);
    return;
  }

  setIsManualTimeChange(true);
  setSelectedTime(new Date(ms), component, true);
  snapLiveSmoothPlaybackToMs(ms);

  setTimeout(() => releaseTimeControl(component), 100);
};

/**
 * Stop the clock WITHOUT touching the live transport — teardown, a zoom hold, a chart reinitialising,
 * a backfill.
 *
 * A live page unmounting must not freeze a live page still open beside it in split view, nor the one
 * being navigated to; it only wants the global clock to stop costing work. Kept as its own name
 * rather than a bare `setIsPlaying(false)` for the intent it carries.
 */
export const stopPlaybackClock = (): void => {
  setIsPlaying(false);
};

/** Live map replay must own the clock; stale VideoSync control must not block playback. */
function canPlaybackPreemptInLiveMode(currentActive: string | null): boolean {
  return liveModeState() && currentActive === 'videosync';
}

// Component priority system - higher number = higher priority
const COMPONENT_PRIORITIES: Record<string, number> = {
  'playback': 2,        // Playback system has lower priority to allow user interactions
  'map': 3,
  'timeseries': 4,      // User interactions have highest priority
  'maptimeseries': 4,   // User interactions have highest priority
  'multimaptimeseries': 4, // Multi-map time series user interactions have highest priority
  'videosync': 4,       // VideoSync timeline interactions have highest priority
  'events': 4,          // Events table user interactions have highest priority
  'tags_explore': 4,    // Tags rail interactions (seek/double-click range focus)
  'fleetmap': 4,        // Fleet map initialization/manual seeks
  'livemaptimeseries': 4, // Live map timeline scrub (click / drag the playhead)
  'alarms': 4,          // Alarms table row -> jump to the alarm instant
  'video': 0,           // Video is now passive (overlay is completely passive, no priority needed)
  'maneuvers-video': 4  // Maneuvers video view can set time when user selects an event
};

// Request control of selectedTime - returns true if granted, false if denied
export const requestTimeControl = (component: string): boolean => {
  const currentActive = getActiveComponentName();
  const currentPriority = currentActive ? COMPONENT_PRIORITIES[currentActive] || 0 : 0;
  const requestedPriority = COMPONENT_PRIORITIES[component] || 0;
  
  // Simplified priority log - only show key info
  if (!currentActive || requestedPriority >= currentPriority) {
    // Only log when control is granted or denied, not every request
  }
  
  // Special handling: allow playback to reclaim control when there is no active manual interaction
  if (component === 'playback' && currentActive && (currentActive === 'map' || currentActive === 'timeseries' || currentActive === 'maptimeseries')) {
    try {
      if (!getIsManual()) {
        debug(`[PlaybackStore] Granting playback control over '${currentActive}' since no manual interaction is active`);
        setActiveComponent('playback');
        debug(`✅ Component 'playback' granted time control (preempt, no manual activity)`);
        return true;
      }
    } catch (e) {
      // fall through to normal priority logic
    }
  }

  // Live map replay: playback owns the clock; VideoSync must not block (e.g. stale cross-tab sync)
  if (component === 'playback' && canPlaybackPreemptInLiveMode(currentActive)) {
    debug(`[PlaybackStore] Granting playback control over 'videosync' in live mode`);
    setActiveComponent('playback');
    return true;
  }

  // If requester already holds control, no work needed
  if (currentActive === component) {
    return true;
  }

  if (!currentActive || requestedPriority >= currentPriority) {
    setActiveComponent(component);
    // Only log when control changes hands
    if (currentActive) {
      debug(`✅ ${component} took control from ${currentActive}`);
    }
    return true;
  } else {
    // Only log denials for non-playback components
    if (component !== 'playback') {
      debug(`❌ ${component} denied (priority ${requestedPriority} < ${currentPriority})`);
    }
    return false;
  }
};

// Release control of selectedTime
export const releaseTimeControl = (component: string) => {
  if (getActiveComponentName() === component) {
    setActiveComponent(null);
  }
};

// Force release of time control (useful for cleanup)
export const forceReleaseTimeControl = () => {
  const currentActive = getActiveComponentName();
  if (currentActive) {
    debug(`Force releasing time control from '${currentActive}'`);
    setActiveComponent(null);
  }
};

// Check if a component should have time control based on visibility/mounting
export const validateTimeControl = () => {
  const currentActive = getActiveComponentName();
  if (!currentActive) return;
  
  // Check if the active component is still mounted/visible
  // This is a basic check - components should call this periodically
  debug(`Validating time control for '${currentActive}'`);
  
  // For now, we'll rely on components to properly release control
  // In the future, we could add more sophisticated checks here
};

// Guard to prevent multiple simultaneous source fetches
let _isFetchingSources = false;
let _lastSourceFetchAttempt = 0;
const _SOURCE_FETCH_THROTTLE_MS = 10000; // Only try to fetch sources once every 10 seconds

// Throttle "No new data available" log messages
let _lastNoDataLogTime = 0;
const _NO_DATA_LOG_THROTTLE_MS = 5000; // Only log "No new data available" once every 5 seconds

// Live mode: throttle status API fetch to pollIntervalMs
let lastLiveStatusFetchTime = 0;
let cachedLatestTimestamp: number | null = null;

// Live mode: throttle the playback-window history pull (fill gaps when WebSocket is intermittent).
// LIVE_POLL_BASE_MS is 2000, so a healthy feed polls at exactly the fixed cadence this replaced;
// a stalled edge decays to LIVE_POLL_MAX_MS (30s) instead of issuing one wave every 2s forever.
const playbackPullBackoff = createStallBackoff({ baseMs: LIVE_POLL_BASE_MS, maxMs: LIVE_POLL_MAX_MS });
let lastPlaybackPullTime = 0;

// Helper functions to manage the interval
export function startPlaybackInterval(): void {
  clearPlaybackInterval();

  // Armed AFTER the clear above, which tears the watchdog down as part of stopping the clock — arming
  // first would disarm it one line later. Torn down with the interval, because the watchdog exists to
  // reconcile a clock that died while `isPlaying` stayed true, and that is only meaningful while
  // playback is supposed to be running.
  startClockWatchdog();

  // Request control for playback system
  if (!requestTimeControl('playback')) {
    debug(`[PlaybackStore] Failed to get time control for playback, scheduling retry`);
    schedulePlaybackRetry();
    return;
  }

  // Non-live: 100ms tick. The step is wall time since the previous tick × speed, so a late tick
  // still covers the time it missed (1x stays realtime, 2x stays twice realtime).
  const frequencyAnalysis = (window as any).mapFrequencyAnalysis;
  const baseInterval = liveMode() && frequencyAnalysis?.averageInterval
    ? frequencyAnalysis.averageInterval
    : 100; // 100ms tick interval (same at all speeds)
  const pollingInterval = liveMode()
    ? Math.max(50, baseInterval / Math.max(1, playbackSpeed()))
    : baseInterval; // Non-live: fixed interval; live: scale if needed
  _pollingIntervalMs = pollingInterval;
  lastNonLiveTickRealMs = 0;

  startSmoothPlayback();

  const newInterval = setInterval(async () => {
    const currentTime = selectedTime();
    const speed = playbackSpeed();
    const isLiveMode = liveMode();

    if (currentTime instanceof Date && typeof speed === "number" && speed > 0) {
      if (isLiveMode) {
        const pollIntervalMsVal = liveConfigStore.pollIntervalMs();
        const wsBroadcastMsVal = liveConfigStore.wsBroadcastIntervalMs();
        const dataDelayMsVal = liveConfigStore.dataDelayMs();
        const now = Date.now();
        const sourceIds = liveSourcesStore.selectedSourceIds();
        // Phase D4: in gpuMode the columnar path IS the live feed. streamingStore is never
        // initialized (LiveMapContainer returns before initialize()) and the legacy JSON WS server is
        // retired, so both of the loops below have no reader and no writer worth waking — and
        // `isWebSocketRecentlyActive` is permanently false there, because the only thing that stamps
        // it is the WS emit path. Left as-is, that made "the socket is quiet" read true forever and
        // ran a /stream/sources + per-boat /stream/status chain AND a sources x channels
        // /data/merged wave every 2s for the whole session. Worse than the HTTP: the pull bumps
        // historicalDataVersion / newData, which liveFrameSnapshot tracks, so it also forced a full
        // re-window of every source plus the overlays and BSP table downstream of it.
        const gpuLive = config.LIVE_MAP_GPU === true;
        const storeLatestMs = gpuLive
          // Not latestTs(sourceIds): rings are keyed by the server's stream-assigned frame id, not
          // the app source_id. The data edge is the same question across all of them.
          ? liveColumnarStore.latestTsAny()
          : sourceIds.size > 0
            ? streamingStore.getLatestTimestamp(sourceIds)
            : null;
        const wsActive = streamingStore.isWebSocketRecentlyActive(wsBroadcastMsVal + 1000);

        // Status API fallback only when WebSocket has been silent longer than poll interval.
        // Also deferred while the user is waiting on a page load: this is a `/stream/sources` fetch
        // followed by one sequential `/stream/status` per boat, unabortable, and it competes directly
        // with an in-flight import. `lastLiveStatusFetchTime` is deliberately NOT stamped on the
        // yielded path, so the first tick after the window closes fetches immediately rather than
        // waiting out a fresh interval. See utils/renderYield.ts.
        // Its only product is `cachedLatestTimestamp`, the fallback data edge — which gpuLive reads
        // straight out of the columnar store above, so there is nothing left for it to contribute.
        if (!gpuLive && !wsActive && !isRenderYieldActive() && now - lastLiveStatusFetchTime >= pollIntervalMsVal) {
          lastLiveStatusFetchTime = now;
          try {
            const streamResponse = await getData(apiEndpoints.stream.sources);
            if (streamResponse.success && Array.isArray(streamResponse.data)) {
              const sourceNames = streamResponse.data
                .map((s: any) => s.source_name)
                .filter((name: string) => name);
              let latestTimestamp: number | null = null;
              for (const sourceName of sourceNames) {
                try {
                  const statusResponse = await getData(apiEndpoints.stream.sourceStatus(sourceName));
                  if (statusResponse.success && statusResponse.data?.latest_timestamp) {
                    const ts = statusResponse.data.latest_timestamp;
                    if (ts && (!latestTimestamp || ts > latestTimestamp)) {
                      latestTimestamp = ts;
                    }
                  }
                } catch {
                  // Skip this source
                }
              }
              cachedLatestTimestamp = latestTimestamp;
            }
          } catch (err) {
            debug(`[PlaybackStore] Live mode: status fetch error`, err);
          }
        }

        const latestTimeMs = storeLatestMs ?? cachedLatestTimestamp ?? 0;
        const smoothMs = lastSmoothTimeMs;
        const timeDiff = latestTimeMs > 0 ? latestTimeMs - dataDelayMsVal - smoothMs : 0;

        if (latestTimeMs > 0) {
          if (speed >= 5 && timeDiff <= speed * 1000) {
            debug(`[PlaybackStore] Live mode: Reached end of data at ${speed}x speed, reducing to 1x`);
            setPlaybackSpeed(1);
          }
          const RESUME_THRESHOLD_MS = 2000;
          // Gated on `liveFollow`: this auto-resume is what makes `isPlaying` unable to express a
          // user pause on its own — sitting near the data edge is the NORMAL state in live mode, so
          // without the gate the clock restarts itself a second after every deliberate pause. Follow
          // is the half only a gesture writes, so it is the one that decides whether a resume is
          // wanted.
          if (!isPlaying() && liveFollow() && timeDiff <= RESUME_THRESHOLD_MS && timeDiff >= 0) {
            if (isManualTimeChange()) {
              setIsManualTimeChange(false);
            }
            if (!isManualTimeChange()) {
              debug(`[PlaybackStore] Live mode: Time near end, auto-resuming playback`);
              setIsPlaying(true);
            }
          }
        }

        // Sync selectedTime from rAF smooth clock (~2s) for cross-window / timeline labels
        if (isPlaying() && now - lastLiveSelectedTimeSyncMs >= LIVE_SELECTED_TIME_SYNC_MS) {
          lastLiveSelectedTimeSyncMs = now;
          if (smoothMs > 0) {
            setSelectedTime(new Date(smoothMs), 'playback');
          }
        }

        // A live WebSocket IS the healthy case, so clear any accumulated stall while it is serving.
        if (wsActive) {
          playbackPullBackoff.reset();
        }

        // History pull only when WebSocket is not keeping history fresh. Cadence backs off while the
        // data edge is stalled, so a dead feed costs one wave per 30s instead of one every 2s forever.
        //
        // Deferred, not skipped, while the user is waiting on a page load: the wave is sources x
        // channels of HTTP plus the parse and reactive recompute that follow, which is exactly the
        // work that starves an in-flight import. `lastPlaybackPullTime` is deliberately NOT stamped
        // on the yielded path, so the first tick after the window closes (this interval runs every
        // ~100ms) issues the pull immediately instead of waiting out a fresh interval.
        if (!gpuLive && !wsActive && !isRenderYieldActive() && now - lastPlaybackPullTime >= playbackPullBackoff.delayMs()) {
          lastPlaybackPullTime = now;
          // Observed once per ATTEMPT, not per tick: this interval fires every ~100ms, so observing
          // per tick would reach the 30s ceiling within half a second of the normal gap between
          // 5Hz WS batches.
          playbackPullBackoff.observeEdge(latestTimeMs > 0 ? latestTimeMs : null);
          if (sourceIds.size > 0) {
            const windowMin = timeWindow();
            const beforeMs = windowMin > 0 ? Math.max(10000, windowMin * 60 * 1000) : 60000;
            const pullCenterMs = smoothMs > 0 ? smoothMs : currentTime.getTime();
            streamingStore.pullPlaybackWindowFromRedis(sourceIds, pullCenterMs, beforeMs).catch(() => {});
          }
        }
      } else {
        // Non-live: 100ms tick, but the step is the wall time since the previous tick × speed.
        // A busy map delays (and Chrome coalesces) these callbacks; a fixed 100ms step then plays
        // slower than real time. Capped at 1s so a throttled background tab cannot lurch.
        // Not floored at 1x: 0.5x must stay half of wall time or maneuver video seeks fight the clock.
        const nowReal = Date.now();
        const elapsedRealMs = lastNonLiveTickRealMs > 0 ? nowReal - lastNonLiveTickRealMs : pollingInterval;
        const stepMs = nonLivePlaybackStepMs(elapsedRealMs, speed);
        if (stepMs <= 0) return;
        const beforeMs = currentTime.getTime();
        setSelectedTime(new Date(beforeMs + stepMs), 'playback');
        // A sub-20ms step is rejected by setSelectedTime. Leave the anchor so the next tick
        // includes this elapsed time instead of dropping it.
        if (selectedTime().getTime() !== beforeMs) {
          lastNonLiveTickRealMs = nowReal;
        }
      }
    }
  }, pollingInterval);

  if (typeof newInterval === "number") {
    // Removed interval ID log - not useful
    setPlaybackInterval(newInterval);
  }
}

export function clearPlaybackInterval(): void {
  stopClockWatchdog();
  stopSmoothPlayback();
  lastNonLiveTickRealMs = 0;
  const intervalId = playbackInterval();
  if (intervalId !== null && typeof intervalId === "number") {
    clearInterval(intervalId);
    setPlaybackInterval(null); // Reset the interval state
  }
  // Clear any pending retry attempts
  clearPlaybackRetry();
  
  // Release control when playback stops
  releaseTimeControl('playback');
}

// Watch for manual time changes and pause playback
let manualTimeChangeEffectInitialized = false;
let manualTimeChangeEffectDispose: (() => void) | null = null;

export const initializeManualTimeChangeEffect = () => {
  if (manualTimeChangeEffectInitialized) return;
  manualTimeChangeEffectInitialized = true;
  
  if (manualTimeChangeEffectDispose) {
    manualTimeChangeEffectDispose();
  }
  
  manualTimeChangeEffectDispose = createRoot((dispose) => {
    createEffect(() => {
      const isManual = isManualTimeChange();
      if (isManual && isPlaying()) {
        debug(`⏸️ Manual time change detected - pausing playback`);
        setIsPlaying(false);
        clearPlaybackInterval();
        // Do NOT set any restart flag; resume only on explicit user action
      }
    });
    
    return dispose;
  });
};

// Retry logic for starting playback when time control is temporarily unavailable
let playbackRetryTimer: any = null;
const RETRY_DELAY_MS = 150;
const MAX_RETRY_MS = 3000;
let retryStartTime: number | null = null;

function schedulePlaybackRetry(): void {
  if (!isPlaying()) return; // Only retry if user intends to play
  if (playbackRetryTimer) return; // Already scheduled
  if (retryStartTime === null) retryStartTime = Date.now();
  const elapsed = Date.now() - retryStartTime;
  if (elapsed > MAX_RETRY_MS) {
    debug(`[PlaybackStore] Playback retry window expired after ${elapsed}ms`);
    retryStartTime = null;
    return;
  }
  playbackRetryTimer = setTimeout(() => {
    playbackRetryTimer = null;
    startPlaybackInterval();
  }, RETRY_DELAY_MS);
}

function clearPlaybackRetry(): void {
  if (playbackRetryTimer) {
    clearTimeout(playbackRetryTimer);
    playbackRetryTimer = null;
  }
  retryStartTime = null;
}

// Add a function to force sync all playback signals
export function forcePlaybackSync(): void {
  safeSync(syncSelectedTime, 'syncSelectedTime');
  safeSync(syncIsPlaying, 'syncIsPlaying');
  safeSync(syncPlaybackSpeed, 'syncPlaybackSpeed');
  safeSync(syncTimeWindow, 'syncTimeWindow');
  safeSync(syncShowPlayback, 'syncShowPlayback');
}

// Add periodic sync to ensure consistency across components
let periodicSyncInterval: any = null;

export function startPeriodicSync(): void {
  if (periodicSyncInterval) return; // Already running
  
  debug('⏰ PlaybackStore: Event-driven sync enabled (no periodic timer needed)');
  
  // Event-driven sync: Only sync when there are actual changes
  // The sync signals already have autoSync: true, so they handle cross-window sync automatically
  // We only need to ensure sync when there are specific events that might need it
  
  // Sync on window focus (in case we missed updates while tab was inactive)
  const handleWindowFocus = () => {
    if (!isPlaying()) {
      const currentTime = selectedTime();
      const defaultTime = new Date('1970-01-01T12:00:00Z');

      // In live mode: always sync to now (UTC) so playback shows current time
      if (liveMode()) {
        setSelectedTimeState(new Date());
        return;
      }

      // Only sync if current time is default (uninitialized)
      if (currentTime && currentTime.getTime() === defaultTime.getTime()) {
        syncSelectedTimeManual();
      }
    }
  };

  // Sync on visibility change (when tab becomes visible)
  const handleVisibilityChange = () => {
    if (!document.hidden && !isPlaying()) {
      const currentTime = selectedTime();
      const defaultTime = new Date('1970-01-01T12:00:00Z');

      // In live mode: always sync to now (UTC) so playback shows current time
      if (liveMode()) {
        setSelectedTimeState(new Date());
        return;
      }

      // Only sync if current time is default (uninitialized)
      if (currentTime && currentTime.getTime() === defaultTime.getTime()) {
        syncSelectedTimeManual();
      }
    }
  };
  
  // Handle cross-window playback updates
  const handleCrossWindowPlaybackUpdate = (event: CustomEvent) => {
    const payload = (event as any).detail;
    if (!payload) return;
    
    if (payload.type === 'TIME_CHANGE' && payload.selectedTime) {
      debug('🔄 PlaybackStore: Received cross-window time update', payload.selectedTime);
      const newTime = new Date(payload.selectedTime);
      if (!isNaN(newTime.getTime())) {
        // Update without broadcasting back
        setSelectedTimeState(newTime);
        lastSelectedTimeValue = newTime;
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('playbackSelectedTimeChange', { detail: { selectedTime: newTime } }));
          }
        } catch (_) { /* empty */ }
      }
    }
    if (payload.timeWindow !== undefined && Number.isFinite(Number(payload.timeWindow))) {
      setTimeWindowState(Number(payload.timeWindow));
    }
    if (payload.isPlaying !== undefined) {
      debug('🔄 PlaybackStore: Received cross-window isPlaying update', payload.isPlaying);
      setIsPlayingState(payload.isPlaying);
      // When receiving "play" from another window, clear manual time change so the local
      // manual-time-change effect does not immediately pause (which would thrash play/pause).
      if (payload.isPlaying === true) {
        setIsManualTimeChange(false);
      }
    }
  };
  
  // Add event listeners for smarter sync
  window.addEventListener('focus', handleWindowFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('playbackStoreUpdate', handleCrossWindowPlaybackUpdate as EventListener);
  
  // Store cleanup function
  periodicSyncInterval = {
    cleanup: () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('playbackStoreUpdate', handleCrossWindowPlaybackUpdate as EventListener);
    }
  } as any;
}

export function stopPeriodicSync(): void {
  if (periodicSyncInterval) {
    // Handle both old interval and new event-driven cleanup
    if (typeof periodicSyncInterval === 'number') {
      clearInterval(periodicSyncInterval);
    } else if (periodicSyncInterval.cleanup) {
      periodicSyncInterval.cleanup();
    }
    periodicSyncInterval = null;
  }
}

// Cleanup function to clear all sync signals
export function disposePlaybackStore(): void {
  stopPeriodicSync();
  clearPlaybackInterval();
  
  // Dispose of effects
  if (selectionEffectDispose) {
    selectionEffectDispose();
    selectionEffectDispose = null;
    selectionEffectInitialized = false;
  }
  if (isPlayingFollowEffectDispose) {
    isPlayingFollowEffectDispose();
    isPlayingFollowEffectDispose = null;
    isPlayingFollowEffectInitialized = false;
  }
  if (manualTimeChangeEffectDispose) {
    manualTimeChangeEffectDispose();
    manualTimeChangeEffectDispose = null;
    manualTimeChangeEffectInitialized = false;
  }
  
  // Clean up all sync signals - clearSyncData now automatically triggers cleanup
  clearSyncData("showPlayback");
  clearSyncData("selectedTime");
  clearSyncData("isPlaying");
  clearSyncData("playbackSpeed");
  clearSyncData("timeWindow");
  clearSyncData("playbackInterval");
  clearSyncData("videoTime");
  clearSyncData("isManualTimeChange");
  clearSyncData("shouldRestartPlayback");
  clearSyncData("activeComponent");
  clearSyncData("liveMode");
  clearSyncData("fleetMapInstances");
  clearSyncData("fleetMapMountedCount");
  clearSyncData("exploreMapInstances");
  clearSyncData("exploreMapMountedCount");
  fleetMapInstanceController.disposeLocal();
  exploreMapInstanceController.disposeLocal();
}

// This function should be called within a component to properly register cleanup
let appCleanupDispose: (() => void) | null = null;
export function registerPlaybackStoreCleanup(): void {
  if (appCleanupDispose) {
    appCleanupDispose();
  }
  appCleanupDispose = createRoot((dispose) => {
    onCleanup(() => {
      disposePlaybackStore();
    });
    return dispose;
  });
}
